import {
  type BudgetImportResult,
  type BudgetLine,
  type BudgetView,
  can,
  type ProjectDetail,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { FileUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { date, tengeShort, todayLocal } from '../../lib/format';
import { budgetQuery, mln } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { estimatesSummaryQuery } from '../estimates/lib';
import { BudgetChangesPanel } from './BudgetChanges';
import { CompareVersionsPanel } from './CompareVersionsPanel';
import { CostDialog, MarginPanel } from './CostVariants';
import { PaymentBudgetPanel } from './PaymentBudgetPanel';
import { PlanPricesPanel } from './PlanPrices';
import { RatesPanel } from './RatesPanel';
import { ReconcilePanel } from './ReconcilePanel';
import { ResourceCostPanel } from './ResourceCostPanel';
import { SiteCostsPanel } from './SiteCosts';

function DistributeDialog({
  line,
  project,
  versionId,
  onClose,
}: {
  line: BudgetLine;
  project: ProjectDetail;
  versionId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(project.startDate ?? todayLocal());
  const [to, setTo] = useState(project.finishDate ?? '');
  const m = useMutation({
    mutationFn: () => api<BudgetView>(`/budget-lines/${line.id}/distribute`, { body: { from, to } }),
    onSuccess: (v) => {
      qc.setQueryData(budgetQuery(project.id, versionId).queryKey, v);
      for (const k of ['budget', 'portfolio', 'attention', 'cashflow', 'project'])
        qc.invalidateQueries({ queryKey: [k] });
      toast('Раздел разложен по неделям');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Разложить по неделям"
      description={`${line.code ? `${line.code}. ` : ''}${line.name} — ${tengeShort(line.effectiveAmount)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!from || !to} onClick={() => m.mutate()}>
            Разложить поровну
          </Button>
        </>
      }
    >
      <p className="mb-4 text-[14px] text-ink-2">
        Сумма раздела делится поровну между неделями периода; потом любую неделю можно поправить в Excel и
        загрузить новую версию. Уже внесённые недели раздела заменяются.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="С недели">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="По неделю">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

function ImportButton({
  project,
  onDone,
}: {
  project: ProjectDetail;
  onDone: (r: BudgetImportResult) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const m = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api<BudgetImportResult>(`/projects/${project.id}/budget/import`, { form });
    },
    onSuccess: onDone,
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) m.mutate(f);
          e.target.value = '';
        }}
      />
      <Button
        variant="secondary"
        icon={<FileUp className="size-4" />}
        loading={m.isPending}
        onClick={() => ref.current?.click()}
      >
        Загрузить из Excel
      </Button>
    </>
  );
}

export function BudgetTab({ project }: { project: ProjectDetail }) {
  const me = useMe();
  const qc = useQueryClient();
  const search = useSearch({ strict: false }) as { line?: string };
  const target = {
    legalEntityId: project.legalEntityId,
    directionId: project.directionId,
    projectId: project.id,
    projectPath: [project.parentId],
  };
  const canEdit = can(me.access, 'budget.edit', target);
  const canApprove = can(me.access, 'budget.approve', target);
  const [versionId, setVersionId] = useState<string | undefined>();
  const q = useQuery(budgetQuery(project.id, versionId));
  const [dist, setDist] = useState<BudgetLine | null>(null);
  const [imported, setImported] = useState<BudgetImportResult | null>(null);
  const [showItems, setShowItems] = useState(true);
  const [costLine, setCostLine] = useState<string | null>(null);
  const highlighted = useRef(false);

  const approve = useMutation({
    mutationFn: (id: string) => api<BudgetView>(`/budget-versions/${id}/approve`, { method: 'POST' }),
    onSuccess: () => {
      for (const k of ['budget', 'portfolio', 'attention', 'project'])
        qc.invalidateQueries({ queryKey: [k] });
      toast('Бюджет утверждён');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const select = useMutation({
    mutationFn: (p: { id: string; selected: boolean }) =>
      api<BudgetView>(`/budget-lines/${p.id}/select`, { body: { selected: p.selected } }),
    onSuccess: () => {
      for (const k of ['budget', 'portfolio', 'attention', 'project'])
        qc.invalidateQueries({ queryKey: [k] });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });

  // Переход из «Требует внимания»: подсветить строку и предложить разложить
  useEffect(() => {
    if (!search.line || !q.data || highlighted.current) return;
    highlighted.current = true;
    const line = q.data.lines.find((l) => l.id === search.line);
    if (!line) return;
    document.getElementById(`line-${line.id}`)?.scrollIntoView({ block: 'center' });
    if (canEdit && q.data.current?.status === 'draft' && !line.isDistributed && line.kind === 'section')
      setDist(line);
  }, [search.line, q.data, canEdit]);

  if (!seesMoney(me.access, 'all', target)) {
    return (
      <EmptyState title="Суммы скрыты">Уровень доступа к деньгам назначает владелец компании.</EmptyState>
    );
  }
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const b = q.data;
  const cur = b.current;

  const onImported = (r: BudgetImportResult) => {
    setImported(r);
    setVersionId(r.versionId);
    for (const k of ['budget', 'portfolio', 'attention', 'project', 'cashflow'])
      qc.invalidateQueries({ queryKey: [k] });
    toast(`Загружена ${r.versionName}: ${r.sections} разделов`);
  };

  if (!cur) {
    return (
      <EmptyState
        title="Бюджета ещё нет"
        action={canEdit ? <ImportButton project={project} onDone={onImported} /> : null}
      >
        Загрузите бюджет-график из Excel: лист «График» с колонками «Раздел проекта», суммами и неделями.
        Разделы привяжутся к объектам по шифру ПСД, вехи придут с листа «Потр-ть».
      </EmptyState>
    );
  }

  const lines = b.lines.filter((l) => showItems || l.kind !== 'item');
  const months = [...new Set(b.periods.map((p) => p.month))];
  const sections = b.lines.filter((l) => l.kind === 'section');
  const total = sections.reduce((s, l) => s + l.effectiveAmount, 0);
  const weekTotals = b.periods.map((p) => sections.reduce((s, l) => s + (l.periods[p.start] ?? 0), 0));

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Версия бюджета"
        actions={
          <>
            {canEdit ? <ImportButton project={project} onDone={onImported} /> : null}
            {canApprove && cur.status === 'draft' ? (
              <Button
                variant="primary"
                disabled={!cur.canApprove}
                loading={approve.isPending}
                title={cur.canApprove ? undefined : cur.approveBlockers.join('\n')}
                onClick={() => approve.mutate(cur.id)}
              >
                Утвердить бюджет
              </Button>
            ) : null}
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-4">
          <Select
            aria-label="Версия"
            className="w-72"
            value={cur.id}
            onChange={(e) => setVersionId(e.target.value)}
          >
            {b.versions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} —{' '}
                {v.status === 'approved' ? 'утверждена' : v.status === 'draft' ? 'черновик' : 'заменена'}
              </option>
            ))}
          </Select>
          <Badge
            tone={cur.status === 'approved' ? 'success' : cur.status === 'draft' ? 'warning' : 'neutral'}
          >
            {cur.status === 'approved'
              ? `утверждён ${date(cur.approvedAt)}${cur.approvedBy ? `, ${cur.approvedBy}` : ''}`
              : cur.status === 'draft'
                ? 'черновик'
                : 'заменён'}
          </Badge>
          <span className="text-[14px] text-ink-2">
            итого <b className="num text-ink">{tengeShort(total)}</b>, разложено по неделям{' '}
            <b className="num text-ink">{tengeShort(cur.distributed)}</b>
          </span>
        </div>
        {cur.status === 'draft' && cur.approveBlockers.length ? (
          <div className="mt-4 rounded-md bg-danger-soft px-4 py-3 text-[14px] text-ink">
            <p className="font-medium">
              Утвердить нельзя, пока каждый раздел не разложен по неделям (ПФ-05):
            </p>
            <ul className="mt-1 list-disc pl-5">
              {cur.approveBlockers.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {imported?.issues.length ? (
          <div className="mt-4 rounded-md bg-warning-soft px-4 py-3 text-[14px] text-ink">
            <p className="font-medium">Что нашлось при загрузке файла:</p>
            <ul className="mt-1 list-disc pl-5">
              {imported.issues.map((x) => (
                <li key={x.title}>{x.title}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </Panel>

      {me.tenant?.staff ? <ReconcilePanel versionId={cur.id} /> : null}
      {me.tenant?.staff ? <CompareVersionsPanel current={cur} versions={b.versions} /> : null}
      <EstimatesVsBudget projectId={project.id} sections={sections} onCost={setCostLine} />
      <MarginPanel projectId={project.id} versionId={cur.id} />
      {me.tenant?.staff ? <RatesPanel /> : null}
      <SiteCostsPanel projectId={project.id} versionId={cur.id} />
      <BudgetChangesPanel
        projectId={project.id}
        versionId={cur.id}
        sections={sections.map((s) => ({ id: s.id, name: `${s.code ? `${s.code}. ` : ''}${s.name}` }))}
      />
      <PaymentBudgetPanel projectId={project.id} versionId={cur.id} />
      <ResourceCostPanel projectId={project.id} versionId={cur.id} />
      <PlanPricesPanel projectId={project.id} />

      <Panel
        title="Бюджет по разделам и неделям, млн ₸"
        meta="варианты исполнения не входят в итог, пока не выбраны"
        bodyClassName="p-0"
        actions={
          <label className="flex items-center gap-2 text-[13px] text-ink-2">
            <input type="checkbox" checked={showItems} onChange={(e) => setShowItems(e.target.checked)} />
            статьи разделов
          </label>
        }
      >
        <div className="max-h-[70vh] overflow-auto">
          <table className="text-[13px]">
            <thead className="sticky top-0 z-10 bg-surface-2">
              <tr className="text-left text-[12px] text-ink-2">
                <th
                  rowSpan={2}
                  className="sticky left-0 z-20 min-w-[300px] bg-surface-2 py-2 pr-3 pl-5 font-medium"
                >
                  Раздел, статья
                </th>
                <th rowSpan={2} className="px-2 py-2 font-medium">
                  ЛС
                </th>
                <th rowSpan={2} className="px-2 py-2 text-right font-medium">
                  ТМЦ
                </th>
                <th rowSpan={2} className="px-2 py-2 text-right font-medium">
                  СМР
                </th>
                <th rowSpan={2} className="px-2 py-2 text-right font-medium">
                  Итого
                </th>
                <th rowSpan={2} className="px-2 py-2 font-medium">
                  По неделям
                </th>
                {months.map((mo) => (
                  <th
                    key={mo}
                    colSpan={b.periods.filter((p) => p.month === mo).length}
                    className="border-l border-line px-2 pt-2 text-center font-medium"
                  >
                    {new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(
                      new Date(`${mo}-01`),
                    )}
                  </th>
                ))}
              </tr>
              <tr className="text-[11px] text-ink-2">
                {b.periods.map((p, i) => (
                  <th
                    key={p.start}
                    className={clsx(
                      'px-2 pb-2 text-right font-normal whitespace-nowrap',
                      (i === 0 || b.periods[i - 1]?.month !== p.month) && 'border-l border-line',
                    )}
                    title={`неделя с ${date(p.start)}`}
                  >
                    {p.label.split(', ')[1]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const isSection = l.kind === 'section';
                const isAlt = l.kind === 'alternative';
                return (
                  <tr
                    key={l.id}
                    id={`line-${l.id}`}
                    className={clsx(
                      'border-t border-line',
                      isSection && 'bg-surface',
                      search.line === l.id && 'outline-2 -outline-offset-2 outline-accent',
                      isAlt && !l.isSelected && 'text-ink-2',
                    )}
                  >
                    <td
                      className={clsx(
                        'sticky left-0 z-[1] bg-inherit py-2 pr-3',
                        isSection ? 'pl-5 font-semibold' : 'pl-9',
                      )}
                      style={{ background: 'var(--color-surface)' }}
                    >
                      <div className="flex items-start gap-2">
                        <span className="min-w-0 flex-1">
                          {isSection && l.code ? `${l.code}. ` : ''}
                          {isAlt ? <span className="italic">вариант {l.code}: </span> : null}
                          {l.name}
                          {l.sectionCode && isSection ? (
                            <span className="ml-1 font-normal text-ink-2">· {l.sectionCode}</span>
                          ) : null}
                        </span>
                        {isAlt && !l.code && l.parentId ? (
                          // Вариант из расчёта себестоимости: выбор с обоснованием — в диалоге раздела
                          <button
                            type="button"
                            className="min-h-[44px] shrink-0 text-[12px] font-medium text-accent-text underline-offset-2 hover:underline md:min-h-0"
                            onClick={() => setCostLine(l.parentId)}
                          >
                            {l.isSelected ? 'выбран · себестоимость' : 'себестоимость'}
                          </button>
                        ) : isAlt && canEdit && cur.status === 'draft' ? (
                          <button
                            type="button"
                            className="shrink-0 text-[12px] font-medium text-accent-text underline-offset-2 hover:underline"
                            onClick={() => select.mutate({ id: l.id, selected: !l.isSelected })}
                          >
                            {l.isSelected ? 'отменить выбор' : 'выбрать'}
                          </button>
                        ) : isAlt && l.isSelected ? (
                          <Badge tone="accent">выбран</Badge>
                        ) : null}
                        {isSection ? (
                          <button
                            type="button"
                            className="min-h-[44px] shrink-0 text-[12px] font-normal text-accent-text underline-offset-2 hover:underline md:min-h-0"
                            onClick={() => setCostLine(l.id)}
                          >
                            себестоимость
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="num px-2 py-2 whitespace-nowrap text-ink-2">
                      {isSection ? (l.estimateNo ?? '—') : ''}
                    </td>
                    <td className="num px-2 py-2 text-right">{mln(l.materials)}</td>
                    <td className="num px-2 py-2 text-right">{mln(l.works)}</td>
                    <td className={clsx('num px-2 py-2 text-right', isSection && 'font-semibold')}>
                      {mln(isSection ? l.effectiveAmount : l.amount)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {isSection ? (
                        l.effectiveAmount < 0.5 ? (
                          <span className="text-ink-3">—</span>
                        ) : l.isDistributed ? (
                          <Badge tone="success">да</Badge>
                        ) : canEdit && cur.status === 'draft' ? (
                          <button type="button" onClick={() => setDist(l)} className="inline-flex">
                            <Badge tone="danger">{l.distributed > 0 ? 'частично' : 'нет'} · разложить</Badge>
                          </button>
                        ) : (
                          <Badge tone="danger">{l.distributed > 0 ? 'частично' : 'нет'}</Badge>
                        )
                      ) : null}
                    </td>
                    {b.periods.map((p, i) => {
                      const v = l.periods[p.start];
                      return (
                        <td
                          key={p.start}
                          className={clsx(
                            'num px-2 py-2 text-right whitespace-nowrap',
                            (i === 0 || b.periods[i - 1]?.month !== p.month) && 'border-l border-line',
                            !v && 'text-ink-3',
                          )}
                        >
                          {v ? mln(v) : '·'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="border-t-2 border-line-strong bg-surface-2 font-semibold">
                <td className="sticky left-0 z-[1] bg-surface-2 py-2 pr-3 pl-5">Итого по разделам</td>
                <td />
                <td />
                <td />
                <td className="num px-2 py-2 text-right">{mln(total)}</td>
                <td />
                {weekTotals.map((v, i) => (
                  <td key={b.periods[i]!.start} className="num px-2 py-2 text-right whitespace-nowrap">
                    {v ? mln(v) : '·'}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>
      {dist ? (
        <DistributeDialog line={dist} project={project} versionId={cur.id} onClose={() => setDist(null)} />
      ) : null}
      {costLine ? (
        <CostDialog lineId={costLine} projectId={project.id} onClose={() => setCostLine(null)} />
      ) : null}
    </div>
  );
}

/**
 * Сметная стоимость против бюджета (БМ-01, БМ-08): суммы текущих версий локальных смет по разделам
 * бюджета, к которым они привязаны в карточке сметы, и сметная маржа раздела = смета − бюджет.
 */
function EstimatesVsBudget({
  projectId,
  sections,
  onCost,
}: {
  projectId: string;
  sections: BudgetLine[];
  onCost: (lineId: string) => void;
}) {
  const q = useQuery(estimatesSummaryQuery(projectId));
  if (!q.data?.count) return null;
  const byLine = new Map(q.data.byBudgetLine.map((x) => [x.budgetLineId, x]));
  const linked = sections.filter((l) => byLine.has(l.id));
  const estimateTotal = q.data.amount ?? 0;
  const budgetTotal = sections.reduce((s, l) => s + l.effectiveAmount, 0);
  const margin = estimateTotal - budgetTotal;
  return (
    <Panel
      title="Сметная стоимость и бюджет"
      meta="БМ-01, БМ-08: сметная маржа = смета − плановая себестоимость"
      bodyClassName="p-0"
      actions={
        <Link to="/estimates" search={{ projectId }} className="text-[13px] text-accent-text underline">
          Сметы проекта ({q.data.count})
        </Link>
      }
    >
      <div className="grid grid-cols-2 gap-px bg-line text-[13px] sm:grid-cols-4">
        {[
          { label: 'Смета (текущие версии)', value: tengeShort(estimateTotal) },
          { label: 'Бюджет (себестоимость)', value: tengeShort(budgetTotal) },
          {
            label: 'Сметная маржа',
            value: `${tengeShort(margin)}${estimateTotal ? ` · ${((margin / estimateTotal) * 100).toFixed(1)}%` : ''}`,
            tone: margin < 0 ? 'text-danger' : 'text-success',
          },
          {
            label: 'Смет без раздела бюджета',
            value: tengeShort(q.data.unlinkedAmount),
            tone: q.data.unlinkedAmount ? 'text-warning' : undefined,
          },
        ].map((c) => (
          <div key={c.label} className="bg-surface px-4 py-3">
            <span className="block text-[12px] text-ink-2">{c.label}</span>
            <span className={clsx('num block text-[15px] font-medium', c.tone ?? 'text-ink')}>{c.value}</span>
          </div>
        ))}
      </div>
      {linked.length ? (
        <ul className="divide-y divide-line border-t border-line">
          {linked.map((l) => {
            const e = byLine.get(l.id)!;
            const d = (e.amount ?? 0) - l.effectiveAmount;
            return (
              <li
                key={l.id}
                className="flex flex-col gap-1 px-5 py-2.5 text-[13px] md:flex-row md:items-center md:gap-4"
              >
                <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                  {l.code ? `${l.code}. ` : ''}
                  {l.name}
                  <span className="text-ink-2"> · смет: {e.count}</span>
                </span>
                <span className="num text-ink-2">смета {tengeShort(e.amount)}</span>
                <span className="num text-ink-2">бюджет {tengeShort(l.effectiveAmount)}</span>
                <button
                  type="button"
                  className="min-h-[44px] text-left text-accent-text underline-offset-2 hover:underline md:min-h-0"
                  onClick={() => onCost(l.id)}
                >
                  себестоимость
                </button>
                <span className={clsx('num w-[150px] text-right', d < 0 ? 'text-danger' : 'text-success')}>
                  {d >= 0 ? '+' : ''}
                  {tengeShort(d)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="border-t border-line px-5 py-3 text-[13px] text-ink-2">
          Ни одна смета не привязана к разделу бюджета: раздел выбирается в карточке сметы («Связи»).
        </p>
      )}
    </Panel>
  );
}
